/**
 * Centralized milestone configuration
 *
 * This file defines the standard milestone flow and routing logic.
 * Update this single source of truth when milestone structure changes.
 */

export interface MilestoneDefinition {
  /** Stable slug matching the milestone_types.slug DB column */
  slug: string
  /** Canonical milestone name (as stored in database) */
  name: string
  /** Alternate names or patterns to match (lowercase) */
  namePatterns: string[]
  /** Sequence order in the flow */
  sequenceOrder: number
  /** Whether this milestone is actionable by the athlete (has a page) */
  isActionable: boolean
  /** Path for athlete to submit/work on this milestone (relative to /campaigns/:id) */
  athleteRoute?: string
  /** Brief description */
  description: string
  /** Whether this milestone is only for licensing workflows */
  isLicensingOnly?: boolean
}

/**
 * Standard milestone flow (5 milestones)
 * Sequence: Agree on Terms → Submit Creative Concept → Submit Video → Collaboration Live → License Content
 */
export const MILESTONE_FLOW: MilestoneDefinition[] = [
  {
    slug: 'agree_on_terms',
    name: 'Agree on Terms',
    namePatterns: ['agree on terms', 'offer accepted'],
    sequenceOrder: 0,
    isActionable: false,
    description: 'Negotiation and offer acceptance',
  },
  {
    slug: 'submit_creative_concept',
    name: 'Creative Concept Approval',
    namePatterns: ['creative concept approval', 'creative concept', 'concept'],
    sequenceOrder: 1,
    isActionable: true,
    athleteRoute: '/concept',
    description: 'Creative concept is reviewed and approved by the brand',
  },
  {
    slug: 'submit_video',
    name: 'Video Approval',
    namePatterns: ['video approval', 'submit video', 'video'],
    sequenceOrder: 2,
    isActionable: true,
    athleteRoute: '/upload-review',
    description: 'Final video is reviewed and approved by the brand',
  },
  {
    slug: 'collaboration_live',
    name: 'Live & Published',
    namePatterns: ['live & published', 'collaboration live', 'live'],
    sequenceOrder: 3,
    isActionable: true,
    athleteRoute: '/collaboration-live',
    description: 'Content is published and campaign is live',
  },
  {
    slug: 'license_content',
    name: 'Content Licensed',
    namePatterns: ['content licensed', 'license content', 'licensing', 'ad code'],
    sequenceOrder: 4,
    isActionable: true,
    athleteRoute: '/licensing',
    description: 'Content licensed for advertising use',
    isLicensingOnly: true,
  },
]

/**
 * Get milestone definition by name (case-insensitive)
 */
export function getMilestoneByName(milestoneName: string): MilestoneDefinition | null {
  const lowerName = milestoneName.toLowerCase()
  return (
    MILESTONE_FLOW.find((m) => m.namePatterns.some((pattern) => lowerName.includes(pattern))) ||
    null
  )
}

/**
 * Get athlete route for a milestone by name (returns null if not actionable)
 */
export function getAthleteRouteForMilestone(
  milestoneName: string,
  collaborationId: string,
): string | null {
  const milestone = getMilestoneByName(milestoneName)
  if (!milestone?.isActionable || !milestone.athleteRoute) {
    return null
  }
  return `/collaborations/${collaborationId}${milestone.athleteRoute}`
}

/**
 * Get athlete route for a milestone by slug (returns null if not actionable)
 */
export function getAthleteRouteForMilestoneSlug(
  slug: string,
  collaborationId: string,
): string | null {
  const milestone = MILESTONE_FLOW.find((m) => m.slug === slug)
  if (!milestone?.isActionable || !milestone.athleteRoute) {
    return null
  }
  return `/collaborations/${collaborationId}${milestone.athleteRoute}`
}

/**
 * Check if a milestone is actionable (has a submission page)
 */
export function isMilestoneActionable(milestoneName: string): boolean {
  const milestone = getMilestoneByName(milestoneName)
  return milestone?.isActionable ?? false
}

/**
 * Get all actionable milestones
 */
export function getActionableMilestones(): MilestoneDefinition[] {
  return MILESTONE_FLOW.filter((m) => m.isActionable)
}

/**
 * Get milestone count
 */
export function getMilestoneCount(): number {
  return MILESTONE_FLOW.length
}

/**
 * Get milestones for backend creation (excludes "Agree on Terms" as it's created separately)
 */
export function getMilestonesForCreation(): MilestoneDefinition[] {
  return MILESTONE_FLOW.filter((m) => m.sequenceOrder > 0 && !m.isLicensingOnly)
}
export function getLicensingMilestone(): MilestoneDefinition | undefined {
  return MILESTONE_FLOW.find((m) => m.slug === 'license_content')
}
